import React, {
    createContext,
    useContext,
    useState,
    useCallback,
    useEffect,
} from "react";
import { Bluelagoon } from "@bluelagoon";
const ChatContext = createContext();

export function ChatProvider({ children, apiKey, defaultChatId }) {
    const [appConfiguration, setAppConfiguration] = useState(null);
    const [isLoadingAppConfiguration, setIsLoadingAppConfiguration] =
        useState(true);
    const [messages, setMessages] = useState([
        {
            role: "assistant",
            content: "",
            tool_calls: null,
            createdAt: new Date().toISOString(),
        },
    ]);
    const [isLoading, setIsLoading] = useState(false);
    const [chatId, setChatId] = useState(null);
    const [isReadOnly, setIsReadOnly] = useState(false);
    const [appPreviewSize, setAppPreviewSize] = useState("desktop");

    const getChatHistory = async (savedChatId) => {
        try {
            const response = await Bluelagoon.getChatHistory({
                chatPublicId: savedChatId,
            });

            if (response.status === 200 && response.data.chat?.messages) {
                setMessages(response.data.chat.messages);
            } else {
                console.error("Failed to get chat history:", response);
                setMessages([
                    {
                        role: "assistant",
                        content: "",
                        tool_calls: null,
                        createdAt: new Date().toISOString(),
                    },
                ]);
            }
        } catch (error) {
            console.error("Error fetching chat history:", error);
            setMessages([
                {
                    role: "assistant",
                    content: "",
                    tool_calls: null,
                    createdAt: new Date().toISOString(),
                },
            ]);
        }
    };

    const handleNewChat = async () => {
        setMessages([
            {
                role: "assistant",
                content: "",
                tool_calls: null,
                createdAt: new Date().toISOString(),
            },
        ]);
        setChatId(null);
        window.localStorage.removeItem("chatId");
        handleGetAppConfiguration();
    };

    const handleGetAppConfiguration = async () => {
        // Skip if no apiKey is provided
        if (!apiKey) {
            setIsLoadingAppConfiguration(false);
            return;
        }

        setIsLoadingAppConfiguration(true);
        try {
            const app = await Bluelagoon.getAppFromApiKey({ apiKey });

            if (app.status === 200) {
                const initialMessage =
                    app?.data?.data?.settings?.initialMessage ||
                    "Hi, I am your smart assistant";
                setMessages([
                    {
                        role: "assistant",
                        content: initialMessage,
                        tool_calls: null,
                        createdAt: new Date().toISOString(),
                    },
                ]);
                setAppConfiguration(app.data);
                await initializeChat(initialMessage);
            }
        } catch (error) {
            console.error("Error getting app configuration:", error);
        } finally {
            setIsLoadingAppConfiguration(false);
        }
    };

    const initializeChat = async (initialMessage) => {
        try {
            // If defaultChatId is provided, use it instead of saved chat
            if (defaultChatId) {
                setChatId(defaultChatId);
                await getChatHistory(defaultChatId);
                return;
            }

            // Check if we have a saved chatId
            const savedChatId = window.localStorage.getItem("chatId");
            if (savedChatId) {
                setChatId(savedChatId);
                await getChatHistory(savedChatId);
                return;
            }

            // Only create a new chat if we have an apiKey
            if (apiKey) {
                const response = await Bluelagoon.insertChat({
                    messages: [
                        {
                            role: "assistant",
                            content: initialMessage || "",
                            tool_calls: null,
                            createdAt: new Date().toISOString(),
                        },
                    ],
                    apiKey,
                });

                if (response.data) {
                    const newChatId = response.data.publicId;
                    setChatId(newChatId);
                    window.localStorage.setItem("chatId", newChatId);
                }
            }
        } catch (error) {
            console.error("Error initializing chat:", error);
        }
    };

    const sendMessage = useCallback(
        async (content) => {
            if (isReadOnly) {
                console.warn("Cannot send messages in read-only mode");
                return;
            }

            try {
                setIsLoading(true);
                const userMessage = {
                    role: "user",
                    content: content,
                    tool_calls: null,
                    createdAt: new Date().toISOString(),
                };
                const messageId = Date.now().toString();
                setMessages((prev) => [...prev, userMessage]);

                console.log(
                    "Sending request to:",
                    `${Bluelagoon.apiUrl}/chat-dynamic`
                );

                const response = await fetch(
                    `${Bluelagoon.apiUrl}/chat-dynamic`,
                    {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                        },
                        body: JSON.stringify({
                            apiKey,
                            userMessage,
                            chatPublicId: chatId,
                        }),
                    }
                );

                if (!response.ok) {
                    throw new Error(
                        `Network response was not ok: ${response.status}`
                    );
                }

                const reader = response.body.getReader();

                // We track partial chunks of content for the assistantMessage
                let assistantMessage = {
                    role: "assistant",
                    content: "",
                    id: messageId,
                    tool_calls: null,
                    createdAt: new Date().toISOString(),
                };

                // We'll keep track of whether we are currently in a tool call
                let chunkHolder = "";
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;

                    const chunk = new TextDecoder().decode(value);
                    const lines = chunk.split("\n");
                    for (const line of lines) {
                        console.log("line:", line);
                        if (
                            line.startsWith("data: ") ||
                            chunkHolder.startsWith("data: ")
                        ) {
                            try {
                                let data;
                                try {
                                    data = JSON.parse(line.slice(5));
                                    chunkHolder = "";
                                } catch (error) {
                                    chunkHolder += line;
                                    try {
                                        console.log(
                                            "chunkHolder:",
                                            chunkHolder
                                        );
                                        data = JSON.parse(chunkHolder.slice(5));
                                        chunkHolder = "";
                                    } catch (error) {
                                        console.error(
                                            "Error parsing data:",
                                            error
                                        );
                                    }
                                }

                                // If it's a standard chunked content, append it
                                if (data && !data.type && data.content) {
                                    assistantMessage.content += data.content;
                                } else if (data) {
                                    switch (data.type) {
                                        case "tool_calling":
                                            // Mark that we are in a tool call

                                            if (!assistantMessage.tool_calls) {
                                                assistantMessage.tool_calls =
                                                    [];
                                            }
                                            assistantMessage.tool_calls.push({
                                                id: assistantMessage.tool_calls
                                                    .length,
                                                name: data.tool.name,
                                                arguments: data.tool.arguments,
                                                result: null,
                                            });
                                            break;

                                        case "tool_result":
                                            // End of tool call

                                            if (assistantMessage.tool_calls) {
                                                const lastToolCall =
                                                    assistantMessage.tool_calls[
                                                        assistantMessage
                                                            .tool_calls.length -
                                                            1
                                                    ];
                                                if (lastToolCall) {
                                                    lastToolCall.result =
                                                        data.content;
                                                }
                                            }
                                            break;

                                        case "tool_error":
                                            // Also end the tool call on error

                                            assistantMessage.tool_calls =
                                                assistantMessage.tool_calls ||
                                                [];
                                            assistantMessage.tool_calls.push({
                                                type: "tool_error",
                                                content: data.content,
                                                tool: data.tool,
                                                timestamp:
                                                    new Date().toISOString(),
                                            });
                                            assistantMessage.content +=
                                                data.content;
                                            break;

                                        case "content":
                                            assistantMessage.content +=
                                                data.content;
                                            // Buffer
                                            break;

                                        default:
                                            if (data.content) {
                                                assistantMessage.content +=
                                                    data.content;
                                            }
                                            break;
                                    }
                                }

                                if (data) {
                                    setMessages((prev) => {
                                        const newMessages = [...prev];
                                        const lastMessage =
                                            newMessages[newMessages.length - 1];

                                        if (
                                            lastMessage.role === "assistant" &&
                                            lastMessage.id === messageId
                                        ) {
                                            lastMessage.content =
                                                assistantMessage.content;
                                            lastMessage.tool_calls =
                                                assistantMessage.tool_calls;
                                            return newMessages;
                                        } else {
                                            return [
                                                ...newMessages,
                                                assistantMessage,
                                            ];
                                        }
                                    });
                                }

                                // If we get a new chatId from the server, set it
                                if (data?.chatId) {
                                    setChatId(data.chatId);
                                }
                            } catch (parseError) {
                                console.error(
                                    "Error parsing SSE data:",
                                    parseError,
                                    line
                                );
                            }
                        } else {
                        }
                    }
                }
            } catch (error) {
                console.error("Chat error:", error);
                setMessages((prev) => [
                    ...prev,
                    {
                        role: "assistant",
                        content:
                            "Sorry, there was an error processing your request.",
                        tool_calls: null,
                        createdAt: new Date().toISOString(),
                    },
                ]);
            } finally {
                setIsLoading(false);
            }
        },
        [messages, chatId, isReadOnly]
    );

    React.useEffect(() => {
        if (apiKey) {
            handleGetAppConfiguration();
        }
    }, [apiKey]);

    React.useEffect(() => {
        if (defaultChatId) {
            setChatId(defaultChatId);
            getChatHistory(defaultChatId);
            setIsReadOnly(true);
        } else {
            setIsReadOnly(false);
            setMessages([
                {
                    role: "assistant",
                    content: "",
                    tool_calls: null,
                    createdAt: new Date().toISOString(),
                },
            ]);
        }
    }, [defaultChatId]);

    return (
        <ChatContext.Provider
            value={{
                isLoadingAppConfiguration,
                messages,
                isLoading,
                sendMessage,
                chatId,
                handleNewChat,
                isReadOnly,
                appPreviewSize,
                setAppPreviewSize,
            }}
        >
            {children}
        </ChatContext.Provider>
    );
}

export const useChat = () => {
    const context = useContext(ChatContext);
    if (!context) {
        throw new Error("useChat must be used within a ChatProvider");
    }
    return context;
};
